/**
 * The setup-only mark, for React (src/lib/signInSecurity/setupSession.ts holds the one subscription; the app starts it).
 *
 *   useSignInSetupSession  the whole reading
 *   useSignInSetupHold     true while this session must finish setting up: every protected page goes to
 *                          /sign-in-setup and the app's always-on readers stand down (src/App.tsx)
 *   useSignInSetupReading  true while the signed-in session's token is being read (bounded by the store), so a page
 *                          guard waits for the answer instead of opening a page the gate may move away from
 *   useUidClearOfSetup     the uid a reader may read Firestore with: only once the signed-in session's token was read
 *                          and carries no mark (the uid as given while the watch is off), so a reader keyed by the
 *                          signed-in person never opens a listener the rules would refuse
 *   useSetupStandDown      the same answer as a flag, for a reader whose own "no uid" branch does something (clears)
 *
 * Nothing here imports Firebase: a header or a provider that reads the mark never loads firebase/auth by doing so.
 */
import { useSyncExternalStore } from 'react';
import { readingClear, readSetupSession, subscribeSetupSession, type SetupSession } from '../lib/signInSecurity/setupSession';

export function useSignInSetupSession(): SetupSession {
  return useSyncExternalStore(subscribeSetupSession, readSetupSession, readSetupSession);
}

export function useSignInSetupHold(): boolean {
  const s = useSignInSetupSession();
  return s.active && s.held;
}

export function useSignInSetupReading(): boolean {
  const s = useSignInSetupSession();
  return s.active && !s.checked;
}

export function useUidClearOfSetup(uid: string | null | undefined): string | null {
  const standDown = useSetupStandDown(uid);
  return uid && !standDown ? uid : null;
}

/**
 * True while a reader keyed by `uid` must wait: the watch is running and no signed-in token has been read clear of the
 * mark yet (it is being read, it carries the mark, or the watch has not seen this sign-in at all). False with no uid (a
 * reader's own signed-out branch is its own business) and while the watch is off.
 */
export function useSetupStandDown(uid: string | null | undefined): boolean {
  const s = useSignInSetupSession();
  if (!uid || !s.active) return false;
  return !readingClear(s);
}
